import axios from 'axios'
import { getServerConfig } from '../apiResolver.js'

/**
 * FarmavelAPI Class
 * Maneja todas las comunicaciones REST con el nuevo backend de Farmavel (Laravel).
 * Implementa autenticación Sanctum mediante tokens Bearer.
 */
class FarmavelAPI {
    constructor() {
        this.baseUrl = this._resolveBaseUrl()

        this.client = axios.create({
            baseURL: this.baseUrl,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        })

        // Interceptor para incluir el token de Sanctum en cada petición
        this.client.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('farmavel_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => Promise.reject(error)
        )
    }

    getBaseUrl() {
        return this.baseUrl
    }

    /**
     * Determina la URL base según el hostname actual para el nuevo backend Laravel.
     * @private
     */
    _resolveBaseUrl() {
        const { api_base, server_base } = getServerConfig()
        const normalizedApiBase = (api_base || `${server_base}api/`).replace(
            /\/?$/,
            '/'
        )

        return normalizedApiBase
    }

    /**
     * Manejador central de peticiones.
     * @private
     */
    async _request(method, path, data = null, config = {}) {
        try {
            const requestConfig = {
                method,
                url: path,
                ...config,
            }

            if (data) {
                if (method.toLowerCase() === 'get') {
                    requestConfig.params = data
                } else {
                    requestConfig.data = data
                }
            }

            const response = await this.client.request(requestConfig)
            return response.data
        } catch (error) {
            return this._handleError(error)
        }
    }

    /**
     * Manejo centralizado de errores.
     * @private
     */
    _handleError(error) {
        const errorDetail = {
            message:
                error.response?.data?.message || // Laravel suele usar 'message' en lugar de 'msg'
                error.message ||
                'Unknown API Error',
            status: error.response?.status,
            data: error.response?.data,
        }

        console.error('[FarmavelAPI Error]:', errorDetail)
        throw errorDetail
    }

    // Métodos REST Públicos

    get(path, params = {}, config = {}) {
        return this._request('get', path, params, config)
    }

    post(path, data = {}, config = {}) {
        return this._request('post', path, data, config)
    }

    put(path, data = {}, config = {}) {
        return this._request('put', path, data, config)
    }

    patch(path, data = {}, config = {}) {
        return this._request('patch', path, data, config)
    }

    delete(path, data = null, config = {}) {
        return this._request('delete', path, data, config)
    }
}

// Exportar instancia única
export const farmavel = new FarmavelAPI()
